import { InfoCard, Progress } from '@backstage/core-components';
import { TextField, Typography, Button, FormControl } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Alert, AlertTitle } from '@material-ui/lab';
import { TenantDropdown } from '../TenantDropdown/TenantDropdown';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { UserManagement } from './UserManagement';
import { SimpleFormValues, SimpleSchema } from './schema';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formGroup: {
      padding: theme.spacing(2),
    },
    formControl: {
      margin: theme.spacing(1),
      minWidth: 300,
    },
    button: {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    customWidth: {
      maxWidth: 300,
    },
    errorAlert: {
      whiteSpace: 'pre-line',
      margin: 10,
      maxWidth: 500,
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    margin: {
      marginTop: 20,
      marginBottom: 5,
    },
  }),
);

export enum aadGroupStatus {
  awaitingInput,
  processing,
  success,
  failure,
}

const defaultValues = {
  activeDirectoryGroupName: '',
  tenant: '',
  users: [],
};

type FormProps = {
  status: aadGroupStatus;
  outputMsg: string;
  onSubmit: (formValues: SimpleFormValues) => void;
};

export const SimpleForm = ({ status, outputMsg, onSubmit }: FormProps) => {
  const classes = useStyles();

  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    formState: { isValid, errors },
  } = useForm<SimpleFormValues>({
    defaultValues,
    mode: 'onChange',
    shouldUnregister: true,
    resolver: yupResolver(SimpleSchema),
  });

  const submitProgress = () => {
    if (status === aadGroupStatus.awaitingInput) {
      return null;
    } else if (status === aadGroupStatus.processing) {
      return <Progress />;
    } else if (status === aadGroupStatus.success) {
      return (
        <Alert severity="success">
          <AlertTitle>{outputMsg}</AlertTitle>
        </Alert>
      );
    }
    return <Alert severity="error">Failed to create Active Directory Group.{outputMsg}</Alert>;
  };

  return (
    <InfoCard>
      <Typography style={{ marginTop: 10, marginBottom: 10 }}>
        {' '}
        To create an Azure Active Directory Group, please fill out the following fields{' '}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl variant="outlined" className={classes.formControl}>
          <TextField
            id="AAD Group Name"
            label="Example= AAD_HELLO_WORLD"
            {...register('activeDirectoryGroupName')}
            style={{ width: 300 }}
            inputProps={{ 'data-testid': 'activeDirectoryGroupName' }}
            variant="outlined"
            error={Boolean(errors.activeDirectoryGroupName)}
            helperText={
              Boolean(errors.activeDirectoryGroupName)
                ? errors.activeDirectoryGroupName?.message
                : 'Name of the Azure Active Directory Group'
            }
            required
          />
        </FormControl>
        <div>
          <FormControl variant="outlined" className={classes.formControl}>
            <Controller
              name="tenant"
              control={control}
              render={({ field: { onChange } }) => <TenantDropdown onChange={value => onChange(value.tenant)} />}
            />
            {watch('users') && watch('users').length > 0 && (
              <div style={{ marginTop: 10, marginBottom: 10 }}>
                <Alert severity="warning">
                  <Typography variant="inherit">
                    Changing tenant will remove all added security group members
                  </Typography>
                </Alert>
              </div>
            )}
          </FormControl>
        </div>
        {watch('tenant') && (
          <div className={classes.formControl}>
            <Controller
              name="users"
              control={control}
              render={({ field: { onChange } }) => (
                <UserManagement handleOnClick={value => onChange(value)} tenant={getValues('tenant')} />
              )}
            />
          </div>
        )}
        <div className={classes.formControl}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            name="Submit Request"
            className={classes.button}
            disabled={
              !isValid ||
              String(watch('activeDirectoryGroupName')).length < 3 ||
              String(watch('tenant')).length < 1 ||
              !!errors.activeDirectoryGroupName ||
              status === aadGroupStatus.processing ||
              watch('users').findIndex(user => user.userType === 'Owner') < 0
            }
          >
            Submit Request
          </Button>
          {submitProgress()}
        </div>
      </form>
    </InfoCard>
  );
};
